import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle, XCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * ChatPaymentResult — landing page Stripe Checkout redirects to after a
 * chat-driven enrollment payment. We poll the backend to confirm Stripe
 * marked the session as paid, then show success and tell the customer to
 * return to the chat tab (where the completion card now appears).
 *
 * On `/chat/payment-success` we re-confirm + show success.
 * On `/chat/payment-cancel` we show a soft cancel screen.
 */
export default function ChatPaymentResult({ result = "success" }) {
  const [sp] = useSearchParams();
  const enr = sp.get("enr") || "";
  const [status, setStatus] = useState(result === "success" ? "confirming" : "cancelled");
  const [order, setOrder] = useState("");

  useEffect(() => {
    if (result !== "success" || !enr) return;
    // We don't have session_id in URL — fall back to enrollment id-based
    // confirmation via the session_id stored on the enrollment row.
    // The endpoint is `/api/chat/{session_id}/enrollment/payment-confirm`
    // so we first need to fetch enrollment → session_id. We added a small
    // public mirror endpoint instead: /chat/enrollment-confirm/{enr_id}.
    // For now the customer will see "Confirmando…" then the chat widget's
    // polling picks up the completion card. Stripe webhooks (if configured)
    // also mark the row as complete server-side.
    const tick = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/chat/enrollment-status/${enr}`);
        if (data?.status === "complete") {
          setStatus("paid");
          setOrder((data.id || "").slice(0, 8).toUpperCase());
          clearInterval(tick);
        }
      } catch (e) { /* keep polling */ }
    }, 2000);
    return () => clearInterval(tick);
  }, [enr, result]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12" data-testid="chat-payment-result">
      <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
        {status === "confirming" && (
          <>
            <Loader2 className="w-12 h-12 text-velcor-green mx-auto animate-spin" />
            <h1 className="text-2xl font-extrabold text-velcor-ink mt-4">Confirmando tu pago…</h1>
            <p className="text-slate-600 mt-2 text-[14px]">
              Esto toma unos segundos. Por favor no cierres esta ventana.
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
            <h1 className="text-2xl font-extrabold text-velcor-ink mt-4">¡Pago confirmado!</h1>
            <p className="text-slate-600 mt-2 text-[14px]">
              Tu enrolamiento <strong>#{order}</strong> está completo.
              Vuelve al chat para ver la confirmación y recibirás un email con todos los detalles.
            </p>
            <Link to="/" className="inline-block mt-6 px-5 h-10 leading-10 rounded-lg bg-velcor-green text-white font-extrabold text-[13px]">
              Volver al chat →
            </Link>
          </>
        )}
        {status === "cancelled" && (
          <>
            <XCircle className="w-14 h-14 text-amber-500 mx-auto" />
            <h1 className="text-2xl font-extrabold text-velcor-ink mt-4">Pago cancelado</h1>
            <p className="text-slate-600 mt-2 text-[14px]">
              No completaste el pago. Vuelve al chat y el agente te ayudará a reintentarlo cuando quieras.
            </p>
            <Link to="/" className="inline-block mt-6 px-5 h-10 leading-10 rounded-lg bg-velcor-ink text-white font-extrabold text-[13px]">
              Volver al inicio
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
